/**
 * Grokbot Google Chat routing (GOVERNANCE §19, DASHBOARD.md §1.21).
 *
 * Pure helpers: who a post is addressed to by default, and the exact message
 * Grant sees in the preview and confirms. The server posts that same text
 * (after its own secret and personal-data scan); nothing is added or changed in
 * between.
 */

import { ActionItem, Bot, Snapshot } from './types';

export const CHAT_PEOPLE = ['Cindy Sandor', 'Bing Hauenstein', 'Grant Schick', 'Sandy', 'Waheed', 'general'] as const;
export type ChatPerson = (typeof CHAT_PEOPLE)[number];
export const PERSON_LABEL = (p: string) => (p === 'general' ? 'General team' : p);

export interface ChatDefaults {
  stakeholders: ChatPerson[];
  topic: string;
  /** Where the default came from, shown as "Default for <topic> · GOVERNANCE §19". */
  basis: 'item' | 'snapshot' | 'fallback';
}

const isPerson = (p: string): p is ChatPerson => (CHAT_PEOPLE as readonly string[]).includes(p);

/** Domain defaults for a bot lane, used only when the snapshot has no routing of its own. */
const LANE_FALLBACK: { re: RegExp; topic: string; people: ChatPerson[] }[] = [
  { re: /(social|website|design|competitor)/, topic: 'Social media, website, competitive intelligence', people: ['Cindy Sandor'] },
  { re: /(support)/, topic: 'Customer support', people: ['Sandy'] },
  { re: /(security|product|cpo|backend|mobile|ux|dashboard|ai-models|digital-human|voice|memory|personality|implementation)/, topic: 'Security; software development', people: ['Waheed', 'Grant Schick'] },
  { re: /(^|-)(ceo|cfo|cro|cmo|finance|budget|analyst|growth|marketing|email|content)($|-)/, topic: 'Finance; overall / marketing / brand strategy', people: ['Bing Hauenstein', 'Grant Schick'] },
];

/** Who a Chat post about this item goes to unless Grant changes it. */
export function gchatDefaults(snapshot: Snapshot, bot: Bot, action?: ActionItem | null): ChatDefaults {
  const fromItem = ((action && action.gchat_stakeholders) || []).filter(isPerson);
  if (fromItem.length) {
    return { stakeholders: normalise(fromItem), topic: (action && action.gchat_topic) || 'Other', basis: 'item' };
  }
  const domain = (snapshot.gchat && snapshot.gchat.domains || []).find((d) => (d.bots || []).includes(bot.slug));
  if (domain) {
    return { stakeholders: normalise((domain.stakeholders || []).filter(isPerson)), topic: domain.topic, basis: 'snapshot' };
  }
  const lane = LANE_FALLBACK.find((l) => l.re.test(bot.slug));
  if (lane) return { stakeholders: lane.people, topic: lane.topic, basis: 'fallback' };
  return { stakeholders: ['general'], topic: 'Other', basis: 'fallback' };
}

/** General team stands alone; nobody selected means General team. Never anyone outside the list. */
export function normalise(list: readonly string[]): ChatPerson[] {
  const people = [...new Set(list.filter(isPerson))];
  if (people.length === 0 || people.includes('general')) return ['general'];
  return people;
}

/** Toggle one person in the selection, keeping the rules above. */
export function togglePerson(current: readonly ChatPerson[], person: ChatPerson): ChatPerson[] {
  if (person === 'general') return ['general'];
  const without = current.filter((p) => p !== 'general' && p !== person);
  return normalise(current.includes(person) ? without : [...without, person]);
}

const join = (names: string[]) => (names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`);

/** "For @Cindy Sandor — Topic:" / "For the general team — Topic:". */
export const addressLine = (people: readonly ChatPerson[], topic: string): string => {
  const p = normalise(people);
  return p[0] === 'general' ? `For the general team — ${topic}:` : `For ${join(p.map((x) => `@${x}`))} — ${topic}:`;
};

/** The exact text that is previewed, confirmed and posted. */
export function buildGchatMessage(opts: { people: readonly ChatPerson[]; topic: string; bot: Bot; action: ActionItem }): string {
  const { people, topic, bot, action } = opts;
  const item = (action.for || action.text || 'Untitled item').trim();
  const label = action.recommended_by === 'exec' ? (action.recommended_label || 'exec') : 'bot';
  const source = action.prov && action.prov.path ? `source ${action.prov.path}${action.prov.line ? `:${action.prov.line}` : ''}` : 'source not stated';
  return [
    addressLine(people, topic),
    item,
    `Recommended action (${label}): ${action.recommended_action || 'Not stated in source'}`,
    `Approved by Grant in Bot Ops · ${bot.name} · ${source}`,
  ].join('\n');
}

export type ChatPostStatus = 'queued' | 'posted' | 'failed' | 'blocked' | 'cancelled';
export const CHAT_STATUS_LABEL: Record<ChatPostStatus, string> = {
  queued: 'queued', posted: 'posted', failed: 'failed', blocked: 'blocked by the safety scan', cancelled: 'cancelled',
};
